// src/core/hooks/useSimulado.ts
import { create } from 'zustand';
import { notification } from 'antd';
import { Resolucao, Simulado } from '../../sdk/@types';
import SimuladoService from '../../sdk/services/SimuladoService';
import ResolucaoService from '../../sdk/services/ResolucaoService';
import { success } from 'zod';
import InscritoService from '../../sdk/services/InscritoService';

/** Desfecho da última `fetchSimulado`, para que a view nunca tenha de o re-derivar. */
export type SimuladoLoadOutcome =
  | { status: 'loading' }
  | { status: 'loaded' }
  | { status: 'failed' };

interface SimuladoStore {
  simulado: Simulado.Simulado;
  simuladoOutcome: SimuladoLoadOutcome;
  simulados: Simulado.Simulado[];
  filtereds: Simulado.Simulado[];
  resolucao?: Resolucao.ResolucaoQuestao;
  resolucoes: Resolucao.ResolucaoQuestao[];

  newSimulado: () => void;
  insertSimulado: (data: Simulado.Simulado) => Promise<void>;
  updateSimulado: (data: Simulado.Simulado) => Promise<void>;
  updateResolucao: (data: Resolucao.ResolucaoQuestao) => Promise<void>;  
  finishByUser: () => Promise<void>;  
  changeSituation: (data: any) => Promise<void>;
  fetchSimulado: (id: string) => Promise<void>;
  fetchSimulados: () => Promise<void>;
  fetchSimuladosDoUsuario: () => Promise<Simulado.Simulado[]>;
  fetchResolucoes: () => Promise<void>;

  setSimulado: (s: Simulado.Simulado) => void;
  setResolucao: (r: Resolucao.ResolucaoQuestao) => void;
  setSimulados: (s: Simulado.Simulado[]) => void;
  setFiltereds: (s: Simulado.Simulado[]) => void;
}

export const useSimulado = create<SimuladoStore>((set) => ({
  simulado: { id: '', questions: [] },
  simuladoOutcome: { status: 'loading' },
  simulados: [],
  resolucao: undefined,
  resolucoes: [],
  filtereds: [],

  newSimulado: () => set({ simulado: { id: '', questions: [] }, simuladoOutcome: { status: 'loading' } }),

  insertSimulado: async (data) => {
    const simulado = await SimuladoService.inserirNovoSimulado(data);
    set({ simulado });
    notification.info({ message: 'Sucesso', description: 'Simulado criado com sucesso.' });
  },

  updateSimulado: async (data) => {
    const simulado = await SimuladoService.updateSimulado(data);
    set({ simulado });
    notification.info({ message: 'Sucesso', description: 'Simulado atualizado com sucesso.' });
  },

  finishByUser: async () => {
    await InscritoService.finishSimuladoUsuario();
    notification.info({ message: 'Sucesso', description: 'Simulado finalizado com sucesso.' });
  },

  updateResolucao: async (data) => {
    await ResolucaoService.update(data);

    // Re-fetch resolutions after update
    const resolucoes = await ResolucaoService.getMockTestResolutions();

    // Find the updated one by ID
    const resolucaoUpdated = resolucoes.find(r => r.id === data.id);

    // Update store state
    set({
      resolucoes,
      resolucao: resolucaoUpdated ?? undefined,
    });

  },


  changeSituation: async (data) => {
    const simulado = await SimuladoService.changeSituation(data);
    set({ simulado });
    notification.info({ message: 'Sucesso', description: 'Situação atualizada com sucesso.' });
  },

  fetchSimulado: async (id) => {
    set({ simuladoOutcome: { status: 'loading' } });
    try {
      const simulado = await SimuladoService.getSimulado(id);
      set({ simulado, simuladoOutcome: { status: 'loaded' } });
    } catch {
      // `simulado` fica como estava — nunca sobrescrito por dado parcial.
      set({ simuladoOutcome: { status: 'failed' } });
    }
  },

  fetchSimulados: async () => {
    const simulados = await SimuladoService.getSimulados();
    set({ simulados });
  },

  fetchSimuladosDoUsuario: async () => {
    const simulados = await SimuladoService.getSimuladosDoUsuario();
    set({ simulados });
    return simulados;
  },

  fetchResolucoes: async () => {
    const resolucoes = await ResolucaoService.getMockTestResolutions();
    set({
      resolucoes,
      resolucao: resolucoes[0] ?? undefined,
    });
  },

  setSimulado: (simulado) => set({ simulado }),
  setResolucao: (resolucao) => set({ resolucao }),
  setSimulados: (simulados) => set({ simulados }),
  setFiltereds: (simulados) => set({ simulados }),
}));
